import React, { useState, useEffect, useRef, useMemo } from "react";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import NoData from "components/common/NoData";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    getLessonMonthlyStats,
    getPracticeMonthlyStats,
} from "api/instructorStats";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import "./InstructorMonthlyStats.scss";

dayjs.locale("ko");

const InstructorMonthlyStats = () => {
    const { showError } = useSnackbar();
    const showErrorRef = useRef(showError);
    showErrorRef.current = showError;

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [lessonStats, setLessonStats] = useState(null);
    const [practiceStats, setPracticeStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    const year = selectedDate.year();
    const month = selectedDate.month() + 1;

    useEffect(() => {
        let cancelled = false;
        const fetchStats = async () => {
            setLoading(true);
            setFetchError(false);

            const [lessonRes, practiceRes] = await Promise.all([
                getLessonMonthlyStats(year, month),
                getPracticeMonthlyStats(year, month),
            ]);

            if (cancelled) return;

            if (lessonRes === null && practiceRes === null) {
                setFetchError(true);
                showErrorRef.current("통계를 불러오는 데 실패했습니다.");
            } else if (lessonRes === null) {
                showErrorRef.current("강습 통계를 불러오는 데 실패했습니다.");
            } else if (practiceRes === null) {
                showErrorRef.current("연습반 통계를 불러오는 데 실패했습니다.");
            }

            setLessonStats(lessonRes);
            setPracticeStats(practiceRes);
            setLoading(false);
        };

        fetchStats();
        return () => {
            cancelled = true;
        };
    }, [year, month]);

    const totalSummary = useMemo(() => {
        const lessonSessions = lessonStats?.total_sessions ?? 0;
        const practiceSessions = practiceStats?.total_sessions ?? 0;
        const lessonStudents = lessonStats?.total_students ?? 0;
        const practiceStudents = practiceStats?.total_students ?? 0;
        const lessonAmount = Number(lessonStats?.total_amount) || 0;
        const practiceAmount = Number(practiceStats?.total_amount) || 0;

        return {
            totalSessions: lessonSessions + practiceSessions,
            totalStudents: lessonStudents + practiceStudents,
            totalAmount: lessonAmount + practiceAmount,
        };
    }, [lessonStats, practiceStats]);

    const handlePrevMonth = () => {
        setSelectedDate((prev) => prev.subtract(1, "month"));
    };

    const handleNextMonth = () => {
        const nextMonth = selectedDate.add(1, "month");
        if (nextMonth.isAfter(dayjs(), "month")) return;
        setSelectedDate(nextMonth);
    };

    const isNextDisabled = selectedDate
        .add(1, "month")
        .isAfter(dayjs(), "month");

    const renderDetailList = (stats, type) => {
        const isLesson = type === "lesson";
        const items = isLesson ? stats?.by_lesson : stats?.by_pricing_type;
        if (!items || items.length === 0) return null;

        return (
            <div className="stats-detail-list">
                {items.map((item, idx) => (
                    <div className="detail-item" key={idx}>
                        <div className="detail-name">
                            {isLesson
                                ? `${item.lesson_name}${item.day_order ? ` (${item.day_order}일차)` : ""}`
                                : item.pricing_type_display}
                        </div>
                        <div className="detail-meta">
                            <span className="detail-count">
                                {item.sessions_count}회
                            </span>
                            <span className="detail-students">
                                {item.total_students ?? 0}명
                            </span>
                            <span className="detail-amount">
                                {(item.total_amount ?? 0).toLocaleString("ko-KR")}원
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderSection = (title, stats, type) => {
        const isEmpty = !stats || Object.keys(stats).length === 0;
        const totalAmount = Number(stats?.total_amount) || 0;

        return (
            <div className="stats-section">
                <div className="section-header">
                    <h3 className="section-title">{title}</h3>
                    {!isEmpty && (
                        <span className="section-summary">
                            {stats.total_sessions ?? 0}회 · {stats.total_students ?? 0}명 · {totalAmount.toLocaleString("ko-KR")}원
                        </span>
                    )}
                </div>
                {isEmpty ? (
                    <p className="section-empty">데이터가 없습니다.</p>
                ) : (
                    renderDetailList(stats, type)
                )}
            </div>
        );
    };

    const hasNoData =
        !loading &&
        !fetchError &&
        (!lessonStats || Object.keys(lessonStats).length === 0) &&
        (!practiceStats || Object.keys(practiceStats).length === 0);

    return (
        <div className="instructor-stats-page">
            <SubHeader title="월별 통계" />
            <div className="stats-content">
                <div className="month-navigator">
                    <button
                        className="nav-btn"
                        onClick={handlePrevMonth}
                        aria-label="이전 달"
                    >
                        &lt;
                    </button>
                    <span className="current-month">
                        {selectedDate.format("YYYY년 MM월")}
                    </span>
                    <button
                        className="nav-btn"
                        onClick={handleNextMonth}
                        disabled={isNextDisabled}
                        aria-label="다음 달"
                    >
                        &gt;
                    </button>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : fetchError && !lessonStats && !practiceStats ? (
                    <NoData
                        title="통계를 불러올 수 없습니다"
                        desc="네트워크 상태를 확인하고 다시 시도해주세요."
                    />
                ) : hasNoData ? (
                    <NoData
                        title="통계 데이터가 없습니다"
                        desc={`${selectedDate.format("YYYY년 MM월")}의 기록이 없습니다.`}
                    />
                ) : (
                    <>
                        <div className="total-summary">
                            <div className="total-row">
                                <span className="total-type">강습</span>
                                <div className="total-item">
                                    <span className="total-label">수업</span>
                                    <span className="total-value">{lessonStats?.total_sessions ?? 0}<span className="total-unit">회</span></span>
                                </div>
                                <div className="total-item">
                                    <span className="total-label">인원</span>
                                    <span className="total-value">{lessonStats?.total_students ?? 0}<span className="total-unit">명</span></span>
                                </div>
                                <div className="total-item">
                                    <span className="total-label">매출</span>
                                    <span className="total-value">{(Number(lessonStats?.total_amount) || 0).toLocaleString("ko-KR")}<span className="total-unit">원</span></span>
                                </div>
                            </div>
                            <div className="total-divider" />
                            <div className="total-row">
                                <span className="total-type">연습반</span>
                                <div className="total-item">
                                    <span className="total-label">수업</span>
                                    <span className="total-value">{practiceStats?.total_sessions ?? 0}<span className="total-unit">회</span></span>
                                </div>
                                <div className="total-item">
                                    <span className="total-label">인원</span>
                                    <span className="total-value">{practiceStats?.total_students ?? 0}<span className="total-unit">명</span></span>
                                </div>
                                <div className="total-item">
                                    <span className="total-label">매출</span>
                                    <span className="total-value">{(Number(practiceStats?.total_amount) || 0).toLocaleString("ko-KR")}<span className="total-unit">원</span></span>
                                </div>
                            </div>
                        </div>

                        {renderSection("강습", lessonStats, "lesson")}
                        {renderSection("연습반", practiceStats, "practice")}
                    </>
                )}
            </div>
        </div>
    );
};

export default InstructorMonthlyStats;
